import { useEffect, useRef } from 'react';
import { useSession } from '../contexts/SessionContext';
import { supabase } from '../lib/supabase_client';

declare global {
  interface Window {
    chatwootSDK?: any;
    chatwootSettings?: any;
    $chatwoot?: any;
  }
}

const CHATWOOT_TOKEN = process.env.REACT_APP_CHATWOOT_WEBSITE_TOKEN!;
const CHATWOOT_BASE = process.env.REACT_APP_CHATWOOT_BASE_URL || 'https://app.chatwoot.com';

// 🔒 MODULE-LEVEL GUARDS: Persist across component remounts
// Prevents Chatwoot re-initialization spam when Routes/Sessions update
let CHATWOOT_GLOBAL_INIT = false;
let CHATWOOT_GLOBAL_IDENTIFIED_USER: string | null = null;
let CHATWOOT_GLOBAL_HMAC: any = null;
let CHATWOOT_INIT_IN_PROGRESS = false; // 🔒 Prevent concurrent init calls
let CHATWOOT_HMAC_FETCHED_AT: number = 0; // 🔒 Track when HMAC was fetched
const HMAC_STALE_THRESHOLD_MS = 30 * 60 * 1000; // 30 minutes - re-fetch HMAC if older than this

export const ChatwootWidget = () => {
  const { user, profile, activeOrg } = useSession();
  // ❌ REMOVED selectedCompany - not used, causes unnecessary re-renders on company switch
  const initialized = useRef(false);
  const scriptLoaded = useRef(false);
  const hmacDataRef = useRef<any>(null);
  const checkIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const readyHandlerRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    // ✅ FIX #4: DEV-ONLY safety net to catch removeChild bugs
    // This helps identify if Chatwoot (or any library) is causing removeChild crashes
    if (process.env.NODE_ENV === 'development' && !(Node.prototype.removeChild as any).__patched) {
      const origRemoveChild = Node.prototype.removeChild;
      (Node.prototype.removeChild as any) = function(this: Node, child: Node) {
        if (!this.contains(child)) {
          console.warn('🚨 removeChild on non-child detected!', {
            parent: this,
            child,
            stack: new Error().stack
          });
          return child;
        }
        return origRemoveChild.call(this, child);
      };
      (Node.prototype.removeChild as any).__patched = true;
      console.log('✅ Chatwoot: removeChild safety net installed (dev only)');
    }

    if (!user) {
      cleanup();
      return;
    }

    // 🔒 CRITICAL FIX: Check module-level global instead of component ref
    // This prevents re-initialization even if Routes remount or SessionContext updates
    if (CHATWOOT_GLOBAL_INIT) {
      console.log('Chatwoot: Already initialized globally, checking if user changed...');
      
      // Re-identify if user changed
      if (
        user?.id &&
        CHATWOOT_GLOBAL_IDENTIFIED_USER !== user.id &&
        CHATWOOT_GLOBAL_HMAC
      ) {
        console.log('Chatwoot: User changed, re-identifying...');
        identifyUser(CHATWOOT_GLOBAL_HMAC);
      }
      return;
    }

    // 🔒 Prevent concurrent init calls (SessionContext updates can trigger multiple times)
    if (CHATWOOT_INIT_IN_PROGRESS) {
      console.log('Chatwoot: Init already in progress, skipping...');
      return;
    }

    init();

    async function init() {
      CHATWOOT_INIT_IN_PROGRESS = true; // 🔒 Lock to prevent concurrent init
      
      try {
        // 1️⃣ Get Supabase session
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          console.warn('Chatwoot: No session found');
          CHATWOOT_INIT_IN_PROGRESS = false; // 🔓 Unlock on early return
          return;
        }
    
        // 2️⃣ Fetch HMAC with timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000); // 10s timeout
        
        const res = await fetch(
          `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/chatwoot-hmac`,
          {
            headers: { Authorization: `Bearer ${session.access_token}` },
            signal: controller.signal
          }
        );
        clearTimeout(timeoutId);
    
        if (!res.ok) {
          const errorData = await res.json().catch(() => ({ error: 'Unknown error' }));
          throw new Error(`HMAC fetch failed: ${res.status} - ${errorData.error || 'Unknown error'}`);
        }
    
        const hmacData = await res.json();
        
        if (!hmacData.hmac || !hmacData.identifier) {
          throw new Error('Invalid HMAC data returned');
        }
    
        hmacDataRef.current = hmacData;
        CHATWOOT_GLOBAL_HMAC = hmacData; // 🔒 Cache globally to survive remounts
        CHATWOOT_HMAC_FETCHED_AT = Date.now(); // 🔒 Track fetch time for staleness check
        console.log('Chatwoot: HMAC fetched successfully');
    
        // 3️⃣ Load SDK AFTER HMAC is ready
        // ✅ FIX #3: Check if Chatwoot is already globally available (prevents double injection)
        if (window.chatwootSDK || window.$chatwoot) {
          scriptLoaded.current = true;
          CHATWOOT_GLOBAL_INIT = true; // 🔒 Mark as initialized globally
          CHATWOOT_INIT_IN_PROGRESS = false; // 🔓 Unlock
          await identifyUser(hmacData);
          return;
        }
    
        if (scriptLoaded.current) {
          CHATWOOT_GLOBAL_INIT = true; // 🔒 Mark as initialized globally
          CHATWOOT_INIT_IN_PROGRESS = false; // 🔓 Unlock
          await identifyUser(hmacData);
          return;
        }
    
        // Check if script already exists in DOM
        if (document.getElementById('chatwoot-sdk')) {
          scriptLoaded.current = true;
          CHATWOOT_GLOBAL_INIT = true; // 🔒 Mark as initialized globally
          CHATWOOT_INIT_IN_PROGRESS = false; // 🔓 Unlock
          await identifyUser(hmacData);
          return;
        }
    
        window.chatwootSettings = { 
          hideMessageBubble: true,
          position: 'right',
          type: 'standard',
          launcherTitle: 'Support'
        };
    
        const script = document.createElement('script');
        script.id = 'chatwoot-sdk';
        script.src = `${CHATWOOT_BASE}/packs/js/sdk.js`;
        script.async = true;
        script.defer = true;
    
        script.onload = () => {
          scriptLoaded.current = true;
          
          if (!window.chatwootSDK) {
            console.error('Chatwoot: SDK not available after script load');
            return;
          }
    
          // ✅ IMPROVED: Set up watcher with timeout
          let attempts = 0;
          const maxAttempts = 100; // 5 seconds max (100 * 50ms)
          
          checkIntervalRef.current = setInterval(() => {
            attempts++;
            
            // Timeout after max attempts
            if (attempts >= maxAttempts) {
              if (checkIntervalRef.current) {
                clearInterval(checkIntervalRef.current);
                checkIntervalRef.current = null;
              }
              console.warn('Chatwoot: $chatwoot not available after timeout, will retry on ready event');
              return;
            }
            
            if (window.$chatwoot && window.$chatwoot.setUser && hmacDataRef.current) {
              if (checkIntervalRef.current) {
                clearInterval(checkIntervalRef.current);
                checkIntervalRef.current = null;
              }
              console.log('Chatwoot: $chatwoot available, calling setUser IMMEDIATELY');
              identifyUser(hmacDataRef.current).catch(console.error);
            }
          }, 50);
    
          // Also listen for ready event as backup
          const readyHandler = async () => {
            if (checkIntervalRef.current) {
              clearInterval(checkIntervalRef.current);
              checkIntervalRef.current = null;
            }
            console.log('Chatwoot: ready event fired');
            if (!initialized.current && hmacDataRef.current) {
              await identifyUser(hmacDataRef.current);
            }
          };
          
          readyHandlerRef.current = readyHandler;
          window.addEventListener('chatwoot:ready', readyHandler, { once: true });
    
          // Now run the SDK
          window.chatwootSDK.run({
            websiteToken: CHATWOOT_TOKEN,
            baseUrl: CHATWOOT_BASE,
            hideMessageBubble: true,
            resetAfterLogout: false,
            clearCookiesOnLoad: false
          });
          
          // 🔒 Mark globally initialized after SDK runs successfully
          CHATWOOT_GLOBAL_INIT = true;
        };
    
        script.onerror = () => {
          console.error('Chatwoot: Failed to load SDK script');
        };
        
        // Use setTimeout to ensure React has finished any pending updates
        setTimeout(() => {
          document.body.appendChild(script);
        }, 0);
        const style = document.createElement('style');
        style.innerHTML = `
          .woot-widget-bubble,
          .woot-widget-holder {
            position: relative !important;
            overflow: visible !important;
          }
          
          /* Position close button at bottom-right of viewport */
          .woot-widget-holder .woot--close-button,
          .woot-widget-holder button[aria-label*="close"],
          .woot-widget-holder button[aria-label*="Close"],
          .woot--close-button,
          [class*="woot-close"],
          [class*="woot-close-button"] {
            position: absolute !important;
            bottom: -40px !important;
            right: 0 !important;
            z-index: 10001 !important;
          }
          
          /* Ensure widget container doesn't constrain close button */
          .woot-widget-holder {
            overflow: visible !important;
          }
        `;
        
        // 🔒 Mark init complete
        CHATWOOT_INIT_IN_PROGRESS = false;
      } catch (e) {
        console.error('Chatwoot init failed', e);
        CHATWOOT_INIT_IN_PROGRESS = false; // 🔓 Unlock on error
      }
    }

    async function identifyUser(hmacData: any) {
      if (!window.$chatwoot || !user) {
        console.warn('Chatwoot: Cannot identify - widget or user not available');
        return;
      }
    
      // 🔒 CRITICAL FIX: Check module-level global - prevents duplicate identify calls
      const identifier = hmacData.identifier || user.id;
      if (CHATWOOT_GLOBAL_IDENTIFIED_USER === identifier) {
        console.log('Chatwoot: User already identified globally, skipping');
        return;
      }
    
      // ✅ FIX #1: Check if Chatwoot DOM is still present before touching it
      const widgetHolder = document.querySelector('.woot-widget-holder');
      if (!widgetHolder || !document.body.contains(widgetHolder)) {
        console.warn('Chatwoot: widget DOM missing, abort identify');
        return;
      }
    
      try {
        
        const currentIdentifier = window.$chatwoot.getAttribute?.('identifier') || 
                                   localStorage.getItem('chatwoot:identifier');
        
        if (currentIdentifier && currentIdentifier !== identifier) {
          console.log('Chatwoot: Switching users, hiding widget (NOT resetting)');
          // ✅ FIX #2: NEVER call reset() automatically - it causes removeChild crashes
          // Instead, just hide the widget. Reset only on manual logout.
          try {
            window.$chatwoot.toggleBubbleVisibility('hide');
            // Wait a bit before re-identifying to avoid race conditions
            await new Promise(resolve => setTimeout(resolve, 500));
          } catch (e) {
            console.warn('Chatwoot: Failed to hide widget during user switch', e);
          }
        }
        
        // Prepare user data
        const userData: any = {
          identifier_hash: hmacData.hmac,
          email: user.email || '',
          name: activeOrg?.orgName || '',
        };
    
        let phoneNumber = hmacData.phone_number || (profile as any)?.contact_number || null;
        if (phoneNumber) {
          phoneNumber = phoneNumber.toString().replace(/[\s\-]/g, '');
          if (!phoneNumber.startsWith('+')) {
            if (phoneNumber.startsWith('60')) {
              phoneNumber = '+' + phoneNumber;
            } else {
              phoneNumber = '+60' + phoneNumber;
            }
          }
          userData.phone_number = phoneNumber;
        }
    
        const setUserResult = window.$chatwoot.setUser(identifier, userData);
        
        if (setUserResult && typeof setUserResult.then === 'function') {
          await setUserResult;
        } else if (setUserResult === false) {
          throw new Error('setUser returned false');
        }
    
        try {
          localStorage.setItem('chatwoot:identifier', identifier);
        } catch (e) {
          // Ignore localStorage errors
        }
    
        if (window.$chatwoot.setCustomAttributes) {
          const customAttrs: any = {
            timestamp: Date.now()
          };
          
          if (activeOrg?.orgId) {
            customAttrs.org_id = activeOrg.orgId;
          }
          
          window.$chatwoot.setCustomAttributes(customAttrs);
        }
    
        initialized.current = true;
        CHATWOOT_GLOBAL_IDENTIFIED_USER = identifier; // 🔒 Mark user as identified globally
        console.log('Chatwoot: ✅ User identification successful');
        
        // Show widget with retry (Chatwoot bubble DOM can take time to be ready)
        let showAttempts = 0;
        const maxShowAttempts = 5;
        const tryShowWidget = () => {
          showAttempts++;
          try {
            if (window.$chatwoot?.toggleBubbleVisibility) {
              window.$chatwoot.toggleBubbleVisibility('show');
              console.log('Chatwoot: Widget shown successfully');
            }
          } catch (e) {
            if (showAttempts < maxShowAttempts) {
              // Retry after delay
              setTimeout(tryShowWidget, 500);
            } else {
              console.warn('Chatwoot: Failed to show widget after', maxShowAttempts, 'attempts (non-critical)');
            }
          }
        };
        
        setTimeout(tryShowWidget, 300);
      } catch (e) {
        console.error('Chatwoot: setUser failed', e);
        // Note: We reset component ref but NOT globals
        // This allows retry without re-initializing the entire SDK
        initialized.current = false;
      }
    }

    function cleanup() {
      // 🔒 CRITICAL: DO NOT reset module-level globals!
      // These must persist across component remounts to prevent spam
      // ❌ DO NOT: CHATWOOT_GLOBAL_INIT = false
      // ❌ DO NOT: CHATWOOT_GLOBAL_IDENTIFIED_USER = null
      // ❌ DO NOT: CHATWOOT_GLOBAL_HMAC = null
      
      // Only clear component-level refs
      hmacDataRef.current = null;
      
      // Clear interval if it exists
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
        checkIntervalRef.current = null;
      }
      
      // Remove event listener if it exists
      if (readyHandlerRef.current) {
        window.removeEventListener('chatwoot:ready', readyHandlerRef.current);
        readyHandlerRef.current = null;
      }
      
      if (window.$chatwoot) {
        try {
          // Only hide, don't reset - this preserves chat history
          window.$chatwoot.toggleBubbleVisibility('hide');
          // Don't call reset() here - we want to keep the conversation state
        } catch (e) {
          // Ignore errors during cleanup
        }
      }
      
      // ✅ DON'T clear localStorage - this contains conversation data
      // Only clear if you want to force a fresh start
      // The identifier will be checked on next login to see if we need to reset
    }

    return cleanup;
  }, [user?.id]) // ✅ Only watch user ID - prevents re-runs on SessionContext object updates

  // 🔒 Update custom attributes when org changes (without re-initializing)
  useEffect(() => {
    if (!user || !CHATWOOT_GLOBAL_INIT || !window.$chatwoot?.setCustomAttributes) return;

    const customAttrs: any = {
      timestamp: Date.now()
    };
    
    if (activeOrg?.orgId) {
      customAttrs.org_id = activeOrg.orgId;
    }
    
    if (activeOrg?.orgName) {
      customAttrs.org_name = activeOrg.orgName;
    }
    
    try {
      window.$chatwoot.setCustomAttributes(customAttrs);
    } catch (e) {
      // Silently fail if widget not ready
    }
  }, [activeOrg?.orgId, user?.id]);

  // 🔒 EVENT-DRIVEN VISIBILITY: Reshow widget on tab return/focus AND refresh HMAC if stale
  useEffect(() => {
    if (!user) return;

    let isRefreshingHmac = false; // Prevent concurrent refresh

    const handleVisibilityReturn = async () => {
      if (document.visibilityState !== 'visible') return;
      if (!window.$chatwoot) return;


      
      try {
        // 🔒 CHECK 1: Is HMAC stale? (older than threshold)
        const hmacAge = Date.now() - CHATWOOT_HMAC_FETCHED_AT;
        const isHmacStale = hmacAge > HMAC_STALE_THRESHOLD_MS;

        if (isHmacStale && !isRefreshingHmac) {
          console.log(`Chatwoot: HMAC is ${Math.round(hmacAge / 60000)}min old, refreshing...`);
          isRefreshingHmac = true;

          try {
            // Get current session
            const { data: { session } } = await supabase.auth.getSession();
            
            if (!session) {
              console.warn('Chatwoot: No session on visibility return, user may need to re-login');
              isRefreshingHmac = false;
              return;
            }

            // Fetch fresh HMAC
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 10000);

            const res = await fetch(
              `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/chatwoot-hmac`,
              {
                headers: { Authorization: `Bearer ${session.access_token}` },
                signal: controller.signal
              }
            );
            clearTimeout(timeoutId);

            if (!res.ok) {
              console.warn('Chatwoot: HMAC refresh failed, keeping old HMAC');
              isRefreshingHmac = false;
              return;
            }

            const hmacData = await res.json();

            if (!hmacData.hmac || !hmacData.identifier) {
              console.warn('Chatwoot: Invalid HMAC data on refresh');
              isRefreshingHmac = false;
              return;
            }

            // Update cached HMAC
            CHATWOOT_GLOBAL_HMAC = hmacData;
            CHATWOOT_HMAC_FETCHED_AT = Date.now();
            console.log('Chatwoot: HMAC refreshed successfully');

            // Re-identify user with fresh HMAC
            const identifier = hmacData.identifier || user.id;
            
            // Force re-identify by clearing the global identified user
            // This ensures we send fresh HMAC to Chatwoot
            CHATWOOT_GLOBAL_IDENTIFIED_USER = null;

            if (window.$chatwoot?.setUser) {
              const userData: any = {
                identifier_hash: hmacData.hmac,
                email: user.email || '',
                name: activeOrg?.orgName || '',
              };

              let phoneNumber = hmacData.phone_number || (profile as any)?.contact_number || null;
              if (phoneNumber) {
                phoneNumber = phoneNumber.toString().replace(/[\s\-]/g, '');
                if (!phoneNumber.startsWith('+')) {
                  phoneNumber = phoneNumber.startsWith('60') ? '+' + phoneNumber : '+60' + phoneNumber;
                }
                userData.phone_number = phoneNumber;
              }

              window.$chatwoot.setUser(identifier, userData);
              CHATWOOT_GLOBAL_IDENTIFIED_USER = identifier;
              console.log('Chatwoot: ✅ Re-identified user with fresh HMAC');
            }
          } catch (e) {
            console.warn('Chatwoot: HMAC refresh error', e);
          } finally {
            isRefreshingHmac = false;
          }
        }

        // Reshow widget if hidden (regardless of HMAC refresh)
        if (CHATWOOT_GLOBAL_IDENTIFIED_USER) {
          const widgetHolder = document.querySelector('.woot-widget-holder');
          const isHidden = widgetHolder?.classList.contains('woot-hidden') || 
                          widgetHolder?.classList.contains('woot--hide');

          if (isHidden) {
            requestAnimationFrame(() => {
              try {
                window.$chatwoot?.toggleBubbleVisibility('show');
                console.log('Chatwoot: Reshow on visibility/focus');
              } catch (e) {
                // Silently fail if widget not ready
              }
            });
          }
        }
      } catch (e) {
        // Silently fail if widget not ready
      }
    };

    // Listen to browser events (NOT polling timers)
    document.addEventListener('visibilitychange', handleVisibilityReturn);
    window.addEventListener('focus', handleVisibilityReturn);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityReturn);
      window.removeEventListener('focus', handleVisibilityReturn);
    };
  }, [user, activeOrg?.orgName, profile]);

  return null;
};